import { useDisableTeam } from "@/components/page/disableTeam";
import type { MeetingRunContext } from "./MeetingRunContext";
import { useOutletContext } from "react-router-dom";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { useMemo } from "react";
import type { Attendee, TeamMember } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { TimeField } from "@repo/ui/components/Base/TimeField";
import { Time } from "@internationalized/date";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { meetingRepo, type MeetingDto } from "@/repositories/meetingRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";

export const MeetingRunRollCallSection = () => {
  useDisableTeam();

  const {
    tenantId,
    userMap,
    user,
    team,
    meeting: _meeting,
    mutationSystem,
  } = useOutletContext() as MeetingRunContext;

  const mutationKey = "meeting:" + _meeting.id;

  const meetingMutation: Mutator<MeetingDto> = useFutureCallback(
    async (id, data) => {
      await meetingRepo.update(tenantId, id, data);
    }
  );
  const meeting = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _meeting
  );

  const teamMembersOrdered = useMemo(() => {
    return [
      team.teamMembers.filter(({ class: cls }) => !cls || cls === "voting"),
      team.teamMembers.filter((member) => member.class === "active"),
    ] as [TeamMember[], TeamMember[]];
  }, [team.teamMembers]);

  const startTime = meeting.review?.startTime ?? meeting.startTime;

  const makeMemberButton = (member: TeamMember) => {
    const user = userMap[member.userId];
    if (!user) return;

    const attendance: Attendee = meeting.review?.attendees?.find(
      (attendance) => attendance.userId === user.id
    ) ?? { status: "absent", userId: user.id };

    // update this meeting's attendance
    const update = (status: Attendee["status"]) => {
      const attendees =
        meeting.review?.attendees?.filter(
          (attendance) => attendance.userId !== user.id
        ) ?? [];
      attendees.push({
        userId: user.id,
        status,
      });
      mutationSystem.debounce(
        mutationKey,
        meetingMutation,
        { review: { ...meeting.review, attendees } },
        meeting.id
      );
    };

    // get the next status (implements click-cycling)
    const nextStatus = (status: Attendee["status"]) => {
      const statuses: Record<Attendee["status"], Attendee["status"]> = {
        absent: "present",
        present: "phone",
        phone: "absent",
      };
      return statuses[status];
    };

    const status = attendance.status;

    return (
      <Button
        key={user.id}
        className="mr-2 mb-1 flex-col py-6"
        variant={
          status === "present"
            ? "tertiary"
            : status === "phone"
              ? "secondary"
              : "accent"
        }
        onClick={() => update(nextStatus(status))}
      >
        {user.firstName} {user.lastName}
        <br />
        <span className="text-xs">
          {status === "present"
            ? "Present"
            : status === "phone"
              ? "Remote"
              : "Absent"}
        </span>
      </Button>
    );
  };

  return (
    <div className="w-full">
      <h1 className="text-3xl mb-2">Roll Call</h1>

      <div className="flex flex-row gap-2 items-center">
        <p>Meeting Start Time:</p>
        <TimeField
          disableHasTimeChange
          value={new Time(startTime.getHours(), startTime.getMinutes())}
          onChange={(time) => {
            const start = startTime;

            const newStart = new Date(
              start.getFullYear(),
              start.getMonth(),
              start.getDate(),
              time.hour,
              time.minute
            );

            mutationSystem.debounce(
              mutationKey,
              meetingMutation,
              { review: { ...meeting.review, startTime: newStart } },
              meeting.id
            );
          }}
        />
      </div>

      <SelectInput
        label="Chairman"
        value={meeting.review?.chairman ?? user.id}
        onValueChange={(chairman) => {
          mutationSystem.debounce(
            mutationKey,
            meetingMutation,
            { review: { ...meeting.review, chairman } },
            meeting.id
          );
        }}
        options={team.teamMembers.map((member) => {
          const user = userMap[member.userId];
          return (
            <SelectItem key={member.userId} value={member.userId}>
              {user?.firstName} {user?.lastName}
            </SelectItem>
          );
        })}
      />

      <p className="mt-4 text-base">Members present:</p>
      <p className="my-2">
        Click once to mark present. Click twice to mark attending by phone.
        Click three times to reset to absent.
      </p>

      <p className="mb-1">Voting Members</p>
      <div className="mb-5">{teamMembersOrdered[0].map(makeMemberButton)}</div>

      <p className="mb-1">Non-voting Members</p>
      <div>{teamMembersOrdered[1].map(makeMemberButton)}</div>
    </div>
  );
};
